(() => {
  const SELECTORS = {
    productAddForm: 'form[action*="/cart/add"]',
    addons: '[data-addons]',
    warrantyCheckbox: '[data-addon-warranty]',
    removalCheckbox: '[data-addon-removal]',

    // Any likely click target, but we only intercept when it belongs to a /cart/add form and add-ons exist
    atcCandidate: 'button, input, a',

    // Your theme’s drawer
    cartDrawer: '#drawer-cart, [data-drawer="drawer-cart"], nav.cart__drawer',

    // Drawer subtotal element from your HTML
    drawerTotal: '[data-cart-final]',
  };

  function debug(...args) {
    console.log('[product-addons]', ...args);
  }

  // Prevent double-adds if the theme fires multiple handlers
  let isAdding = false;

  function nextTick() {
    return new Promise((resolve) => requestAnimationFrame(() => resolve()));
  }

  function isInsideCartDrawer(el) {
    return !!el?.closest?.(SELECTORS.cartDrawer);
  }

  function isCheckoutButton(el) {
    if (!el) return false;

    // Your checkout button: <button type="submit" name="checkout" ...>
    if (el.getAttribute('name') === 'checkout') return true;

    // Any submit inside the drawer checkout form action="/cart"
    const inCartForm = el.closest('form[action="/cart"]');
    if (inCartForm && (el.getAttribute('type') === 'submit' || el.tagName === 'BUTTON')) return true;

    return false;
  }

  function isAddToCartTrigger(el) {
    if (!el) return false;

    // Links are not add-to-cart triggers in this theme
    if (el.tagName === 'A') return false;

    const type = (el.getAttribute('type') || '').toLowerCase();
    const name = (el.getAttribute('name') || '').toLowerCase();

    if (name === 'checkout') return false;

    if (el.tagName === 'BUTTON') {
      // Many themes use: <button> (no type), type="submit", or type="button"
      return type === '' || type === 'submit' || type === 'button';
    }

    if (el.tagName === 'INPUT') {
      return type === 'submit' || type === 'button';
    }

    return false;
  }

  function findProductFormFromTarget(target) {
    const nearest = target?.closest?.(SELECTORS.productAddForm);
    if (nearest) return nearest;

    return document.querySelector(SELECTORS.productAddForm);
  }

  function findAddonsNearTarget(target, form) {
    const insideForm = form?.querySelector(SELECTORS.addons);
    if (insideForm) return insideForm;

    const wrapper = target?.closest?.(
      '[data-product-section], .product, .product__info, .product__main, .product-single, .product-page'
    );
    if (wrapper) {
      const near = wrapper.querySelector(SELECTORS.addons);
      if (near) return near;
    }

    return null;
  }

  function getVariantIdFromForm(form) {
    const idInput = form?.querySelector('input[name="id"]');
    return idInput?.value || null;
  }

  function getQuantityFromForm(form) {
    const qtyInput = form?.querySelector('input[name="quantity"]');
    const qty = qtyInput ? parseInt(qtyInput.value, 10) : 1;
    return Number.isFinite(qty) && qty > 0 ? qty : 1;
  }

  function getAddons(addonsEl) {
    if (!addonsEl) return null;

    const warrantyVariantId = addonsEl.dataset.warrantyVariantId || null;
    const removalVariantId = addonsEl.dataset.removalVariantId || null;

    const wantsWarranty = !!addonsEl.querySelector(SELECTORS.warrantyCheckbox)?.checked;
    const wantsRemoval = !!addonsEl.querySelector(SELECTORS.removalCheckbox)?.checked;

    return { warrantyVariantId, removalVariantId, wantsWarranty, wantsRemoval };
  }

  async function addItems(items) {
    debug('Adding items', items);

    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items })
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err?.description || 'Could not add to cart');
    }

    return res.json();
  }

  async function getCart() {
    const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
    if (!res.ok) throw new Error('Could not load cart');
    return res.json();
  }

  function formatMoneyAUD(cents) {
    const dollars = (cents / 100).toFixed(2);
    return `$${dollars} AUD`;
  }

  function hasCartDrawer() {
    return !!document.querySelector(SELECTORS.cartDrawer);
  }

  function openCartDrawerIfPossible() {
    // Theme uses data-drawer-toggle="drawer-cart" (also on close button)
    const toggle = document.querySelector('[data-drawer-toggle="drawer-cart"]');
    const drawer = document.querySelector(SELECTORS.cartDrawer);

    const isVisible = drawer?.classList?.contains('drawer--visible');
    if (!isVisible && toggle) toggle.click();
  }

  async function updateDrawerTotalFromCart() {
    if (!hasCartDrawer()) return;
    const totalEl = document.querySelector(SELECTORS.drawerTotal);
    if (!totalEl) return;

    const cart = await getCart();
    totalEl.textContent = formatMoneyAUD(cart.total_price);
  }

  async function refreshCartUI() {
    document.dispatchEvent(new CustomEvent('cart:refresh'));
    document.dispatchEvent(new CustomEvent('theme:cart:refresh'));
    document.dispatchEvent(new CustomEvent('ajaxCart:refresh'));
    document.dispatchEvent(new CustomEvent('cart:updated'));

    await updateDrawerTotalFromCart();

    if (hasCartDrawer()) {
      openCartDrawerIfPossible();
      return;
    }

    window.location.href = '/cart';
  }

  async function maybeInterceptAddToCart(controlEl) {
    if (isInsideCartDrawer(controlEl)) return false;
    if (isCheckoutButton(controlEl)) return false;
    if (!isAddToCartTrigger(controlEl)) return false;

    const form = findProductFormFromTarget(controlEl);
    if (!form) return false;

    // Only intercept if the clicked control is within a /cart/add form.
    // Prevents catching random buttons elsewhere.
    if (!controlEl.closest(SELECTORS.productAddForm)) return false;

    // Only intercept when add-ons exist for this product
    const addonsEl = findAddonsNearTarget(controlEl, form);
    if (!addonsEl) return false;

    // Lock to prevent double-adds
    if (isAdding) return true;
    isAdding = true;

    try {
      // Key fix: allow checkbox state and theme UI updates to settle
      await nextTick();
      // If you still see a rare miss, uncomment this second tick:
      // await nextTick();

      const addons = getAddons(addonsEl);
      if (!addons) return false;

      const mainVariantId = getVariantIdFromForm(form);
      const quantity = getQuantityFromForm(form);

      debug('Intercepted add to cart', { mainVariantId, quantity, addons });

      if (!mainVariantId) {
        alert('Could not determine selected variant.');
        return true;
      }

      const items = [{ id: mainVariantId, quantity }];

      // Warranty per fridge
      if (addons.wantsWarranty && addons.warrantyVariantId) {
        items.push({
          id: addons.warrantyVariantId,
          quantity,
          properties: { 'Add-on for': String(mainVariantId) }
        });
      }

      // Removal once per order
      if (addons.wantsRemoval && addons.removalVariantId) {
        items.push({
          id: addons.removalVariantId,
          quantity: 1,
          properties: { 'Add-on for': String(mainVariantId) }
        });
      }

      await addItems(items);
      await refreshCartUI();
      return true;
    } finally {
      isAdding = false;
    }
  }

  function bindClickDelegation() {
    if (document.documentElement.dataset.addonsClickBound === 'true') return;
    document.documentElement.dataset.addonsClickBound = 'true';

    document.addEventListener('click', async (e) => {
      const controlEl = e.target?.closest?.(SELECTORS.atcCandidate);
      if (!controlEl) return;

      try {
        const intercepted = await maybeInterceptAddToCart(controlEl);
        if (intercepted) {
          e.preventDefault();
          e.stopPropagation();
        }
      } catch (err) {
        console.error(err);
        alert(err.message);
      }
    }, true);
  }

  function bindSubmitCapture() {
    // Capture submit too, in case theme submits the form (some do even with AJAX)
    document.addEventListener('submit', async (e) => {
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!form.matches(SELECTORS.productAddForm)) return;

      const addonsEl = form.querySelector(SELECTORS.addons);
      if (!addonsEl) return;

      // If no add-ons, let normal submit happen
      const addons = getAddons(addonsEl);
      if (!addons) return;

      e.preventDefault();
      e.stopPropagation();

      if (isAdding) return;
      isAdding = true;

      try {
        await nextTick();
        // If you still see a rare miss, uncomment:
        // await nextTick();

        const mainVariantId = getVariantIdFromForm(form);
        const quantity = getQuantityFromForm(form);

        debug('Intercepted submit', { mainVariantId, quantity, addons });

        if (!mainVariantId) {
          alert('Could not determine selected variant.');
          return;
        }

        const items = [{ id: mainVariantId, quantity }];

        if (addons.wantsWarranty && addons.warrantyVariantId) {
          items.push({
            id: addons.warrantyVariantId,
            quantity,
            properties: { 'Add-on for': String(mainVariantId) }
          });
        }

        if (addons.wantsRemoval && addons.removalVariantId) {
          items.push({
            id: addons.removalVariantId,
            quantity: 1,
            properties: { 'Add-on for': String(mainVariantId) }
          });
        }

        await addItems(items);
        await refreshCartUI();
      } catch (err) {
        console.error(err);
        alert(err.message);
      } finally {
        isAdding = false;
      }
    }, true);
  }

  function init() {
    debug('Init');
    bindClickDelegation();
    bindSubmitCapture();
  }

  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
})();